import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

export const WakeAlerts = () => {
  const { projects, setSelectedProject } = useApp();
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/integrations/wake/health`);
      setErrors(res.data?.errors || []);
    } catch (e) {
      // silent: don't disrupt the topbar on transient failures
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  const openProject = (projectId) => {
    const p = projects.find((x) => x.id === projectId);
    if (p) setSelectedProject(p);
    setOpen(false);
    navigate('/integrations');
  };

  if (!errors.length) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 border border-red-500/30"
          data-testid="wake-alerts-trigger"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          <AlertTriangle className="h-4 w-4" />
          <span className="text-xs font-semibold" data-testid="wake-alerts-count">
            {errors.length} {errors.length === 1 ? 'integração Wake com erro' : 'integrações Wake com erro'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="wake-alerts-popover">
        <div className="flex items-center gap-2 border-b p-3">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-sm font-semibold">Integrações Wake com erro</span>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y">
          {errors.map((e) => (
            <div key={e.project_id} className="p-3" data-testid={`wake-alert-${e.project_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{e.project_name}</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 line-clamp-3">
                    {e.sync_message || 'Falha na sincronização com a Wake.'}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">Último sync: {fmtDate(e.last_sync_at)}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 h-7 w-full justify-between text-xs"
                onClick={() => openProject(e.project_id)}
                data-testid={`wake-alert-open-${e.project_id}`}
              >
                Abrir integrações
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
